import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { authApi, formatApiError } from "@/lib/apiClient";
import { markAuthed, markAnon, shouldCheckSession, AUTH_STATUS, getAuthStatus } from "@/lib/authMarker";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    // null = checking, false = not authed, object = employer
    const [employer, setEmployer] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        // If this is the OAuth callback, defer to AuthCallback page
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        // Sprint 1 (2026-02-11) — anonymous-visitor gate. If the local
        // marker says the user is definitely logged out, skip the
        // `/api/auth/me` network call entirely. `UNKNOWN` (no marker
        // yet — first visit, or pre-migration prod user with an
        // existing session cookie) still runs one `/me` to hydrate,
        // then the marker is set for every future mount. See
        // /app/frontend/src/lib/authMarker.js for the rationale.
        // RM delegated entry (2026-06): `?rm_enter=1` forces the /me
        // check — the RM console just minted a session cookie that the
        // local marker knows nothing about.
        const rmEnter = typeof window !== "undefined"
            && new URLSearchParams(window.location.search).get("rm_enter") === "1";
        if (!rmEnter && !shouldCheckSession()) {
            setEmployer(false);
            setLoading(false);
            if (typeof window !== "undefined") window.__BMI_USER__ = null;
            return;
        }
        const isMigration = getAuthStatus() === AUTH_STATUS.UNKNOWN;
        try {
            const me = await authApi.me();
            setEmployer(me);
            markAuthed();
            // P0 outage fix — the ErrorBoundary reads user identity off
            // `window.__BMI_USER__` to attach role + email + company to
            // every crash report so /bmi-ops UI-errors is actionable.
            if (typeof window !== "undefined") window.__BMI_USER__ = me || null;
        } catch {
            setEmployer(false);
            // A 401 during the migration hydration means this visitor
            // is truly anonymous — mark them so future mounts skip
            // the network call. During a genuine session-expired
            // scenario, login() will re-authoritatively set AUTHED.
            markAnon();
            if (typeof window !== "undefined") window.__BMI_USER__ = null;
            // Suppress the noisy expected-401 console error only when
            // we KNOW this was the migration probe (i.e. no prior
            // marker). During normal AUTHED refreshes we let real
            // errors bubble so ops can see genuine token failures.
            if (isMigration) {
                // silent — 401 is expected for anonymous first-time visitors
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const res = await authApi.login(email, password);
        setEmployer(res.employer);
        markAuthed();
        if (typeof window !== "undefined") window.__BMI_USER__ = res.employer || null;
        return res.employer;
    };

    const logout = async () => {
        try {
            await authApi.logout();
        } catch {
            // ignore
        }
        setEmployer(false);
        markAnon();
        if (typeof window !== "undefined") window.__BMI_USER__ = null;
    };

    const refreshMe = async () => {
        try {
            const me = await authApi.me();
            setEmployer(me);
            markAuthed();
            if (typeof window !== "undefined") window.__BMI_USER__ = me || null;
            return me;
        } catch {
            setEmployer(false);
            markAnon();
            if (typeof window !== "undefined") window.__BMI_USER__ = null;
            return null;
        }
    };

    // Sprint 1 (2026-02-11) — wrap the exposed setter so external callers
    // (VerifyOtpPage, AuthCallbackPage, PlansPage, CompleteProfilePage
    // etc.) that assign an employer directly ALSO update the auth marker.
    // Otherwise those flows would leave the marker at ANON, and the very
    // next mount would skip `/me` and treat the user as logged out.
    const setEmployerDirect = (emp) => {
        setEmployer(emp);
        if (emp && typeof emp === "object") {
            markAuthed();
            if (typeof window !== "undefined") window.__BMI_USER__ = emp;
        } else if (emp === false || emp === null) {
            markAnon();
            if (typeof window !== "undefined") window.__BMI_USER__ = null;
        }
    };

    return (
        <AuthCtx.Provider
            value={{
                employer,
                loading,
                login,
                logout,
                refreshMe,
                setEmployer: setEmployerDirect,
                formatError: formatApiError,
            }}
        >
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthCtx);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
